import { EllipsisHorizontalIcon, PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useId, useRef, useState } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import PageContainer from "../../components/PageContainer";
import Sidebar from "../../components/Sidebar";
import FirmFlowTable from "../../components/mytasks/FirmFlowTable";
import { deleteEmployee } from "../../services/employees/deleteEmployee";
import { getEmployees } from "../../services/getEmployees";
import { getLoggedInEmployee } from "../../services/getLoggedInEmployee";
import { usePermissions } from "../../context/PermissionsContext";
import { isAdminRole } from "../../helpers/employeeRole";
import { reissueInvitation } from "../../services/admin/invitations";
import ConfirmationModal from "../../components/Modals/ConfirmationModal";
import DeleteEmployeeModal from "./DeleteEmployeeModal";

function InvitationActionsMenu({ employee, disabled, onReissue }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const id = useId();
    const open = Boolean(anchorEl);

    return (
        <>
            <button
                id={`${id}-button`}
                type="button"
                aria-label={`More actions for ${employee.firstName || employee.email || "employee"}`}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? `${id}-menu` : undefined}
                disabled={disabled}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                onKeyDown={(event) => {
                    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                        event.preventDefault();
                        setAnchorEl(event.currentTarget);
                    }
                }}
                className="flex-none w-10 h-10 mx-2 text-slate-600 rounded border border-slate-200 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-500 disabled:opacity-50"
            >
                <EllipsisHorizontalIcon className="w-5 h-5 mx-auto" aria-hidden="true" />
            </button>
            {/* MUI portals the menu to document.body, outside the table's overflow containers. */}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                MenuListProps={{ id: `${id}-menu`, "aria-labelledby": `${id}-button` }}
            >
                <MenuItem
                    disabled={disabled}
                    onClick={() => {
                        setAnchorEl(null);
                        onReissue(employee);
                    }}
                >
                    Reissue invitation
                </MenuItem>
            </Menu>
        </>
    );
}

function EmployeesPage() {
    const navigate = useNavigate();
    const { can, permissionsLoading } = usePermissions();
    const [invitationEmployee, setInvitationEmployee] = useState(null);
    const [reissuing, setReissuing] = useState(false);
    const reissueInFlight = useRef(false);

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loggedInEmployee, setLoggedInEmployee] = useState(null);
    const [isCurrentEmployeeReady, setIsCurrentEmployeeReady] = useState(false);
    const [list, setList] = useState([]);
    const [employeesToChooseFrom, setEmployeesToChooseFrom] = useState([]);
    const [loading, setLoading] = useState(true);
    const rol = ["User", "Admin"];
    const [employeeToRemoveId, setEmployeeToRemoveId] = useState("");
    const [employeeToDelete, setEmployeeToDelete] = useState(null);
    const [employeeThatReplaces, setEmployeeThatReplaces] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);

    const canAdministerInvitations = !permissionsLoading && isAdminRole(loggedInEmployee?.role) && can("employees.write");

    useEffect(() => {
        callApi();
        getCurrentEmployee();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getCurrentEmployee = async () => {
        try {
            const employee = await getLoggedInEmployee();

            if (employee.success && employee.data?.employeeResponse) {
                setLoggedInEmployee(employee.data.employeeResponse);
            } else {
                setLoggedInEmployee(null);
            }
        } catch {
            setLoggedInEmployee(null);
        } finally {
            setIsCurrentEmployeeReady(true);
        }
    };

    const callApi = async () => {
        try {
            const res = await getEmployees();
            if (res.success && res.data?.success !== false && Array.isArray(res.data?.employees)) setList(res.data.employees);
            else toast.error("Unable to load employees.");
        } catch {
            toast.error("Unable to load employees.");
        } finally {
            setLoading(false);
        }
    };

    const confirmReissue = async () => {
        if (reissueInFlight.current || !invitationEmployee || !canAdministerInvitations ||
            !list.some((employee) => employee.employeeId === invitationEmployee?.employeeId && employee.canReissueInvitation === true)) return;

        reissueInFlight.current = true;
        setReissuing(true);
        try {
            const response = await reissueInvitation(invitationEmployee.employeeId);
            if (response.success === true) {
                toast.success(response.emailSent === true
                    ? "Invitation reissued. The server confirmed the invitation email was sent."
                    : "Invitation reissued. Email delivery was not confirmed.");
                // Discard stale eligibility even if the subsequent refresh fails.
                setList((employees) => employees.map((employee) => employee.employeeId === invitationEmployee.employeeId
                    ? { ...employee, canReissueInvitation: false } : employee));
                await callApi();
            } else {
                toast.error(response.message || "Invitation reissue was not confirmed by the server.");
            }
        } catch {
            toast.error("The server could not be reached. The outcome is unknown; refresh employees before trying again.");
        } finally {
            setInvitationEmployee(null);
            setReissuing(false);
            reissueInFlight.current = false;
        }
    };

    const onHandleClickViewButton = (employeeId) => {
        navigate(`/employees/${employeeId}`);
    };

    const onRemoveEmployee = async (employeeThatReplacesId) => {
        const response = await deleteEmployee(
            employeeToRemoveId,
            employeeThatReplacesId,
        );
        if (response.success) {
            toast.success(response.data.message);
            const newEmployees = list.filter(
                (employee) => employee.employeeId !== employeeToRemoveId,
            );
            setList(newEmployees);
        } else {
            toast.error(response.message);
        }
        setIsModalOpen(false);
    };

    const onHandleClickRemoveButton = (employeeId) => {
        setEmployeeToRemoveId(employeeId);
        const employeeToRemove = list.find(
            (employee) => employee.employeeId === employeeId,
        );
        const employeesToChoose = list.filter(
            (employee) => employee.employeeId !== employeeId,
        );
        setEmployeeToDelete(employeeToRemove);
        setEmployeesToChooseFrom(employeesToChoose);
        setEmployeeThatReplaces(employeesToChoose[0]);
        setIsModalOpen(true);
    };

    return (
        <section>
            <Toaster />
            <ConfirmationModal
                showDialog={Boolean(invitationEmployee) && canAdministerInvitations}
                busy={reissuing}
                onCancel={() => { if (!reissueInFlight.current) setInvitationEmployee(null); }}
                onConfirm={confirmReissue}
                title="Reissue invitation"
                description="Reissue this employee's invitation? The server will verify eligibility again."
            />
            <DeleteEmployeeModal
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                employeeThatReplaces={employeeThatReplaces}
                employeeToDelete={employeeToDelete}
                setEmployeeToReplace={setEmployeeThatReplaces}
                employees={employeesToChooseFrom}
                onRemoveEmployee={onRemoveEmployee}
            />
            <div className="flex h-screen overflow-hidden">
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <PageContainer
                        isLoading={loading || !isCurrentEmployeeReady}
                        loadingText="Loading employees..."
                    >
                        {loggedInEmployee &&
                        typeof loggedInEmployee.role !== "undefined" ? (
                            <main>
                                <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                                    <div className="mb-8 sm:flex sm:justify-between sm:items-center">
                                        <div className="mb-4 sm:mb-0">
                                            <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                                Employees
                                            </h1>
                                        </div>

                                        <div className="grid items-end justify-start grid-flow-col gap-2 sm:auto-cols-max sm:justify-end">
                                            {canAdministerInvitations && (
                                                <button
                                                    onClick={() =>
                                                        navigate("/employees/create")
                                                    }
                                                    className="h-10 text-white bg-teal-500 btn hover:bg-teal-600"
                                                >
                                                    <PlusIcon className="w-5 h-5" />
                                                    <span className="ml-2 xs:block">
                                                        Add Employee
                                                    </span>
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <FirmFlowTable
                                        columns={[
                                            {
                                                Header: "Name",
                                                accessor: "firstName",
                                                Cell: (props) => (
                                                    <a
                                                        rel="noreferrer"
                                                        href={`/employees/${props.row.original.employeeId}`}
                                                        className="rounded-full text-gray hover:text-blue-500"
                                                    >
                                                        {props.value}
                                                    </a>
                                                ),
                                            },
                                            {
                                                Header: "Email",
                                                accessor: "email",
                                                Cell: (props) => (
                                                    <a
                                                        rel="noreferrer"
                                                        href={`/employees/${props.row.original.employeeId}`}
                                                        className="rounded-full text-gray hover:text-blue-500"
                                                    >
                                                        {props.value}
                                                    </a>
                                                ),
                                            },
                                            {
                                                Header: "Role",
                                                accessor: "role",
                                                Cell: (props) => (
                                                    <a
                                                        rel="noreferrer"
                                                        href={`/employees/${props.row.original.employeeId}`}
                                                        className="rounded-full text-gray hover:text-blue-500"
                                                    >
                                                        {rol[props.value] ??
                                                            "Unknown"}
                                                    </a>
                                                ),
                                            },
                                            {
                                                Header: "Actions",
                                                accessor: "employeeId",
                                                Cell: (props) => (
                                                    <div className="flex items-center flex-nowrap whitespace-nowrap">
                                                        {Number(
                                                            loggedInEmployee?.role,
                                                        ) === 1 ? (
                                                            <>
                                                                <button
                                                                    aria-label="Edit employee"
                                                                    onClick={() =>
                                                                        onHandleClickViewButton(
                                                                            props.value,
                                                                        )
                                                                    }
                                                                    className="flex-none h-10 mx-2 text-white bg-teal-500 btn hover:bg-teal-600"
                                                                >
                                                                    <PencilIcon className="w-4 h-4 mx-2" />
                                                                </button>
                                                                <button
                                                                    aria-label="Delete employee"
                                                                    onClick={() =>
                                                                        onHandleClickRemoveButton(
                                                                            props.value,
                                                                        )
                                                                    }
                                                                    className="flex-none h-10 mx-2 text-white bg-red-500 btn hover:bg-red-600"
                                                                >
                                                                    <TrashIcon className="w-4 h-4 mx-2" />
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <p>-</p>
                                                        )}
                                                        {canAdministerInvitations && props.row.original.canReissueInvitation === true && (
                                                            <InvitationActionsMenu
                                                                employee={props.row.original}
                                                                disabled={reissuing}
                                                                onReissue={(employee) => { if (!reissueInFlight.current) setInvitationEmployee(employee); }}
                                                            />
                                                        )}
                                                    </div>
                                                ),
                                            },
                                        ]}
                                        data={list}
                                    />
                                </div>
                            </main>
                        ) : null}
                    </PageContainer>
                </div>
            </div>
        </section>
    );
}

export default EmployeesPage;
